import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Bell, Settings, Clock, Calendar, Send, CheckCircle, XCircle, History, Play, AlertTriangle } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function ReminderSettings({ user, onLogout }) {
  const [settings, setSettings] = useState({
    enabled: true,
    reminder_time: '10:00',
    days_before_due: [7, 3, 1],
    days_after_due: [1, 3, 7],
    exclude_weekends: false,
    max_reminders_per_month: 6,
    min_amount_for_reminder: 1000
  });
  const [logs, setLogs] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [runningAuto, setRunningAuto] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');

  useEffect(() => {
    fetchSettings();
    fetchLogs();
    fetchSchedulerStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/reminder-settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await api.get('/reminder-logs?limit=100');
      setLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSchedulerStatus = async () => {
    try {
      const response = await api.get('/scheduler-status');
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch scheduler status:', error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.post('/reminder-settings', settings);
      toast.success('Settings saved & scheduler updated!');
      fetchSchedulerStatus();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const runAutoReminders = async () => {
    setRunningAuto(true);
    try {
      const response = await api.post('/run-automatic-reminders');
      toast.success(response.data.message);
      fetchLogs();
      fetchSchedulerStatus();
    } catch (error) {
      toast.error('Failed to run reminders: ' + (error.response?.data?.detail || error.message));
    } finally {
      setRunningAuto(false);
    }
  };

  const toggleDayBefore = (day) => {
    const current = settings.days_before_due || [];
    if (current.includes(day)) {
      setSettings({ ...settings, days_before_due: current.filter(d => d !== day) });
    } else {
      setSettings({ ...settings, days_before_due: [...current, day].sort((a, b) => b - a) });
    }
  };

  const toggleDayAfter = (day) => {
    const current = settings.days_after_due || [];
    if (current.includes(day)) {
      setSettings({ ...settings, days_after_due: current.filter(d => d !== day) });
    } else {
      setSettings({ ...settings, days_after_due: [...current, day].sort((a, b) => a - b) });
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'sent') {
      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs"><CheckCircle size={12} /> Sent</span>;
    } else if (status === 'failed') {
      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs"><XCircle size={12} /> Failed</span>;
    }
    return <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">Skipped</span>;
  };

  const getReminderTypeBadge = (type) => {
    if (type.includes('overdue')) {
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Overdue</span>;
    } else if (type === 'on_due_date') {
      return <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">Due Today</span>;
    } else if (type.includes('before')) {
      return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Upcoming</span>;
    } else if (type === 'manual') {
      return <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">Manual</span>;
    }
    return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">{type}</span>;
  };

  // Stats from logs
  const todayLogs = logs.filter(l => new Date(l.sent_at).toDateString() === new Date().toDateString());
  const sentToday = todayLogs.filter(l => l.status === 'sent').length;
  const failedToday = todayLogs.filter(l => l.status === 'failed').length;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Payment Reminder Automation</h1>
            <p className="text-slate-500">Configure automatic WhatsApp payment reminders</p>
          </div>
          <Button 
            onClick={runAutoReminders} 
            disabled={runningAuto}
            className="bg-emerald-600 hover:bg-emerald-700 gap-2"
          >
            <Play size={18} />
            {runningAuto ? 'Running...' : 'Run Now'}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="scheduler-status-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className={`p-3 rounded-xl ${schedulerStatus?.scheduler_running ? 'bg-green-100' : 'bg-gray-100'}`}>
                  <Clock className={schedulerStatus?.scheduler_running ? 'text-green-600' : 'text-gray-400'} size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Scheduler</p>
                  <p className={`font-bold text-sm ${schedulerStatus?.scheduler_running ? 'text-green-600' : 'text-gray-500'}`}>
                    {schedulerStatus?.scheduler_running ? 'Running' : 'Stopped'}
                  </p>
                  {schedulerStatus?.next_run_utc && (
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      Next: {schedulerStatus.reminder_time_ist} IST
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card data-testid="last-run-card">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-blue-100">
                  <Send className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Last Run</p>
                  {schedulerStatus?.last_run_at ? (
                    <>
                      <p className="font-bold text-blue-600 text-sm">
                        {schedulerStatus.last_run_sent} sent
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        {new Date(schedulerStatus.last_run_at).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </>
                  ) : (
                    <p className="font-bold text-slate-400 text-sm">Never</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-emerald-100">
                  <CheckCircle className="text-emerald-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Sent Today</p>
                  <p className="font-bold text-emerald-600">{sentToday}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-red-100">
                  <XCircle className="text-red-600" size={24} />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Failed Today</p>
                  <p className="font-bold text-red-600">{failedToday}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'settings' 
                ? 'text-emerald-600 border-b-2 border-emerald-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Settings size={16} className="inline mr-2" />
            Settings
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'logs' 
                ? 'text-emerald-600 border-b-2 border-emerald-600' 
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <History size={16} className="inline mr-2" />
            History ({logs.length})
          </button>
        </div>

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <Card>
            <CardHeader>
              <CardTitle>Reminder Configuration</CardTitle>
              <CardDescription>Set when and how reminders should be sent</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <Label className="text-base font-medium">Automatic Reminders</Label>
                  <p className="text-sm text-slate-500">Enable/disable automatic reminder system</p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                />
              </div>

              {/* Reminder Time */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Clock size={16} />
                    Reminder Time (IST)
                  </Label>
                  <Input
                    type="time"
                    value={settings.reminder_time}
                    onChange={(e) => setSettings({ ...settings, reminder_time: e.target.value })}
                    className="w-full"
                  />
                  <p className="text-xs text-slate-500">Daily reminders will be sent at this time</p>
                </div>
                <div className="space-y-2">
                  <Label>Minimum Amount (₹)</Label>
                  <Input
                    type="number"
                    value={settings.min_amount_for_reminder}
                    onChange={(e) => setSettings({ ...settings, min_amount_for_reminder: parseFloat(e.target.value) })}
                    className="w-full"
                  />
                  <p className="text-xs text-slate-500">Only send if dues exceed this amount</p>
                </div>
              </div>

              {/* Days Before Due */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <Calendar size={16} />
                  Remind Before Due Date
                </Label>
                <div className="flex flex-wrap gap-2">
                  {[14, 7, 5, 3, 2, 1].map(day => (
                    <button
                      key={day}
                      onClick={() => toggleDayBefore(day)}
                      className={`px-4 py-2 rounded-lg border transition-colors ${
                        settings.days_before_due?.includes(day)
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300'
                      }`}
                    >
                      {day} day{day > 1 ? 's' : ''} before
                    </button>
                  ))}
                </div>
              </div>

              {/* Days After Due (Overdue) */}
              <div className="space-y-3">
                <Label className="flex items-center gap-2 text-red-600">
                  <AlertTriangle size={16} />
                  Remind After Due Date (Overdue)
                </Label>
                <div className="flex flex-wrap gap-2">
                  {[1, 3, 5, 7, 14, 30].map(day => (
                    <button
                      key={day}
                      onClick={() => toggleDayAfter(day)}
                      className={`px-4 py-2 rounded-lg border transition-colors ${
                        settings.days_after_due?.includes(day)
                          ? 'bg-red-600 text-white border-red-600'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-red-300'
                      }`}
                    >
                      {day} day{day > 1 ? 's' : ''} overdue
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Settings */}
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <Label>Exclude Weekends</Label>
                    <p className="text-xs text-slate-500">Don't send on Saturday/Sunday</p>
                  </div>
                  <Switch
                    checked={settings.exclude_weekends}
                    onCheckedChange={(checked) => setSettings({ ...settings, exclude_weekends: checked })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Reminders per Customer/Month</Label>
                  <Input
                    type="number"
                    value={settings.max_reminders_per_month}
                    onChange={(e) => setSettings({ ...settings, max_reminders_per_month: parseInt(e.target.value) })}
                    className="w-full"
                    min="1"
                    max="30"
                  />
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={saveSettings} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Reminder History</CardTitle>
                <CardDescription>Log of all sent reminders</CardDescription>
              </div>
              <Button variant="outline" onClick={fetchLogs} disabled={loading}>
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
            </CardHeader>
            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <History size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No reminders sent yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date/Time</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Mobile</TableHead>
                        <TableHead>Installment</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log) => (
                        <TableRow key={log.id}>
                          <TableCell className="text-sm">
                            {new Date(log.sent_at).toLocaleString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell className="font-medium">{log.customer_name}</TableCell>
                          <TableCell className="text-slate-500">{log.mobile}</TableCell>
                          <TableCell>
                            <span className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded-full font-medium">
                              {log.installment_info || 'N/A'}
                            </span>
                          </TableCell>
                          <TableCell className="font-semibold">₹{log.amount_due?.toLocaleString('en-IN')}</TableCell>
                          <TableCell>{getReminderTypeBadge(log.reminder_type)}</TableCell>
                          <TableCell>
                            {getStatusBadge(log.status)}
                            {log.error_message && (
                              <p className="text-xs text-red-500 mt-1">{log.error_message}</p>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
